import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getAuthMessage } from '../utils/authErrors';
import { Mail, AlertCircle, CheckCircle } from 'lucide-react';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const { resetPassword } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;

    try {
      setError('');
      setMessage('');
      setLoading(true);
      await resetPassword(trimmed);
      setMessage(
        'If an account exists with this email, we sent reset instructions. Check your inbox, spam, and promotions folders.'
      );
    } catch (err: unknown) {
      const raw = err instanceof Error ? err.message.toLowerCase() : '';
      if (raw.includes('rate limit') || raw.includes('too many')) {
        setError('Too many attempts. Please wait a few minutes before trying again.');
      } else {
        setError(getAuthMessage(err));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen min-h-[100dvh] bg-tech-dark text-tech-text flex items-center justify-center px-4 bg-tech-mesh">
      <div className="max-w-md w-full">
        <div className="glass-panel rounded-2xl p-8 sm:p-10 border border-tech-cyan/15 shadow-app-lg ring-1 ring-white/[0.04]">
          <div className="flex justify-center mb-6">
            <Mail className="w-12 h-12 text-tech-cyan" aria-hidden />
          </div>
          <h2 className="text-2xl font-bold text-center mb-2 text-tech-text">Reset Password</h2>
          <p className="text-tech-text-muted text-center mb-8">
            Enter your email and we will send a link to reset your password.
          </p>

          {error && (
            <div className="mb-4 p-3 bg-amber-500/20 border border-amber-500/50 rounded-lg flex items-center gap-2 text-amber-200" role="alert">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
          {message && (
            <div
              className="mb-4 p-3 bg-tech-accent/15 border border-tech-accent/40 rounded-lg flex items-start gap-2 text-tech-accent text-sm"
              role="status"
            >
              <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" aria-hidden />
              <span>{message}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="forgot-email" className="block text-tech-text-muted mb-2 font-medium">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-tech-text-muted" aria-hidden />
                <input
                  id="forgot-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full pl-10 pr-4 py-3 rounded-xl bg-tech-gray border border-tech-border text-tech-text placeholder-tech-text-muted/50 focus:outline-none focus:ring-2 focus:ring-tech-cyan/50 focus:border-tech-cyan"
                  placeholder="Enter your email"
                  autoComplete="email"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-xl font-semibold text-tech-dark hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-tech-cyan/50"
              style={{ background: 'linear-gradient(135deg, #0066ff 0%, #00d9ff 100%)' }}
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/login" className="text-tech-cyan hover:text-tech-cyan-dark text-sm font-semibold">
              Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
